import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowsClockwise,
  ArrowUUpLeft,
  Broadcast,
  CheckCircle,
  Lightning,
  Plus,
  Skull,
  Trash,
  WarningOctagon,
} from "@phosphor-icons/react";
import { Webhooks } from "../lib/api";

const STATUS_CHIP = {
  delivered: { cls: "chip", label: "OK" },
  pending: { cls: "chip", label: "PENDING" },
  pending_retry: { cls: "chip", label: "RETRY" },
  in_flight: { cls: "chip chip-blue", label: "IN FLIGHT" },
  failed: { cls: "chip chip-live", label: "FAILED" },
  dead: { cls: "chip chip-live", label: "DEAD" },
};

const REPLAYABLE = new Set(["failed", "dead", "pending_retry"]);

const fmtIso = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toISOString().replace("T", " ").slice(11, 19) + "Z";
  } catch {
    return s;
  }
};

const fmtFullIso = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toISOString().replace("T", " ").slice(0, 19) + "Z";
  } catch {
    return s;
  }
};

export default function WebhooksPage() {
  const [catalogue, setCatalogue] = useState([]);
  const [hooks, setHooks] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [sources, setSources] = useState([]);

  const [newUrl, setNewUrl] = useState("https://httpbin.org/post");
  const [newSecret, setNewSecret] = useState("");
  const [newEvents, setNewEvents] = useState(new Set());

  const [testHookId, setTestHookId] = useState("");
  const [testEvent, setTestEvent] = useState("photo.uploaded");
  const [testMode, setTestMode] = useState("synthetic");
  const [persist, setPersist] = useState(false);
  const [sourceEventId, setSourceEventId] = useState("");
  const [firing, setFiring] = useState(false);

  const loadCore = async () => {
    try {
      const [cat, wh, dlv] = await Promise.all([
        Webhooks.eventCatalogue(),
        Webhooks.list(),
        Webhooks.recentDeliveries(50),
      ]);
      setCatalogue(cat.items || []);
      setHooks(wh || []);
      setDeliveries(dlv.items || []);
    } catch (e) {
      toast.error("LOAD FAILED");
    }
  };

  useEffect(() => {
    loadCore();
    const t = setInterval(() => {
      Webhooks.recentDeliveries(50).then((d) => setDeliveries(d.items || [])).catch(() => {});
    }, 3000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (testMode !== "real") {
      setSources([]);
      return;
    }
    Webhooks.eventSources(testEvent, 30)
      .then((res) => setSources(res.items || []))
      .catch(() => setSources([]));
  }, [testMode, testEvent]);

  const toggleNewEvent = (ev) => {
    setNewEvents((prev) => {
      const next = new Set(prev);
      if (next.has(ev)) next.delete(ev);
      else next.add(ev);
      return next;
    });
  };

  const registerHook = async () => {
    if (!newUrl.trim()) return toast.error("URL REQUIRED");
    if (newEvents.size === 0) return toast.error("PICK AT LEAST 1 EVENT");
    try {
      const payload = { url: newUrl.trim(), events: Array.from(newEvents), active: true };
      if (newSecret.trim()) payload.secret = newSecret.trim();
      const created = await Webhooks.create(payload);
      toast.success(`HOOK REGISTERED · ${created.id.slice(0, 8)}`, {
        description: `Signing secret (copy now, shown once): ${created.secret}`,
        duration: 30000,
      });
      setNewUrl("https://httpbin.org/post");
      setNewSecret("");
      setNewEvents(new Set());
      await loadCore();
    } catch {
      toast.error("REGISTER FAILED");
    }
  };

  const removeHook = async (id) => {
    try {
      await Webhooks.remove(id);
      toast.success("HOOK DELETED");
      await loadCore();
    } catch {
      toast.error("DELETE FAILED");
    }
  };

  const fireTest = async () => {
    if (!testHookId) return toast.error("SELECT A WEBHOOK");
    const body = { mode: testMode };
    if (testMode === "real") {
      if (sourceEventId) body.source_event_id = sourceEventId;
      else body.event = testEvent;
    } else {
      body.event = testEvent;
      body.persist = persist;
    }

    setFiring(true);
    try {
      const res = await Webhooks.fireTest(testHookId, body);
      toast.success(
        `FIRED · ${res.mode.toUpperCase()} · ${res.event}`,
        {
          description: [
            res.delivery_id ? `delivery ${res.delivery_id.slice(0, 8)}` : null,
            res.persisted_to_audit ? "audit: persisted" : "audit: bypassed",
          ].filter(Boolean).join(" · "),
        }
      );
      const d = await Webhooks.recentDeliveries(50);
      setDeliveries(d.items || []);
      if (testMode === "real") {
        Webhooks.eventSources(testEvent, 30).then((r) => setSources(r.items || []));
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "FIRE FAILED";
      toast.error(String(msg).toUpperCase());
    } finally {
      setFiring(false);
    }
  };

  const replay = async (deliveryId) => {
    try {
      const res = await Webhooks.replayDelivery(deliveryId);
      toast.success(`REPLAYED · ${res.event} · was ${String(res.prev_status).toUpperCase()}`);
      const d = await Webhooks.recentDeliveries(50);
      setDeliveries(d.items || []);
    } catch (e) {
      const msg = e?.response?.data?.detail || "REPLAY FAILED";
      toast.error(String(msg).toUpperCase());
    }
  };

  return (
    <div className="px-4 py-6 max-w-5xl mx-auto page-fade" data-testid="webhooks-page">
      <div className="flex items-end justify-between mb-6 gap-3 flex-wrap">
        <div>
          <p className="label-mono text-neutral-500">98 / WEBHOOK SANDBOX</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
            WEBHOOKS
          </h1>
          <p className="text-xs font-mono text-neutral-500 mt-1">
            REGISTER · FIRE-TEST · SNAPSHOT REPLAY — DEV CONTROL PLANE
          </p>
        </div>
        <button onClick={loadCore} className="btn-flat-outline" data-testid="webhooks-refresh-btn">
          <ArrowsClockwise weight="bold" size={16} />
          Refresh
        </button>
      </div>

      {/* SECTION A — Register */}
      <section className="hairline-2 mb-6 p-4" data-testid="webhooks-register-section">
        <h2 className="font-display font-black text-2xl tracking-tight mb-3 flex items-center gap-2">
          <Plus weight="bold" size={20} /> REGISTER SUBSCRIBER
        </h2>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="label-mono block mb-1">CALLBACK URL</label>
            <input
              data-testid="webhooks-new-url"
              className="w-full px-3 py-2 border-2 border-black font-mono text-sm"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              placeholder="https://yourapp.tld/webhooks/aisuite"
            />
          </div>
          <div>
            <label className="label-mono block mb-1">SHARED SECRET (optional)</label>
            <input
              data-testid="webhooks-new-secret"
              className="w-full px-3 py-2 border-2 border-black font-mono text-sm"
              value={newSecret}
              onChange={(e) => setNewSecret(e.target.value)}
              placeholder="auto-generated if blank"
            />
          </div>
        </div>
        <p className="label-mono mb-2">SUBSCRIBE TO EVENTS</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {catalogue.map((e) => {
            const on = newEvents.has(e.event);
            return (
              <button
                key={e.event}
                onClick={() => toggleNewEvent(e.event)}
                className={`chip ${on ? "chip-live" : ""}`}
                data-testid={`webhooks-event-${e.event.replace(/\./g, "-")}`}
                title={e.description}
              >
                {e.event}
              </button>
            );
          })}
        </div>
        <button onClick={registerHook} className="btn-flat" data-testid="webhooks-register-btn">
          <Broadcast weight="bold" size={16} />
          Register Hook
        </button>
      </section>

      {/* SECTION B — Existing hooks */}
      <section className="hairline-2 mb-6 p-4" data-testid="webhooks-list-section">
        <h2 className="font-display font-black text-2xl tracking-tight mb-3 flex items-center gap-2">
          <Broadcast weight="bold" size={20} /> REGISTERED ({hooks.length})
        </h2>
        {hooks.length === 0 ? (
          <p className="font-mono text-sm text-neutral-500">no webhooks yet</p>
        ) : (
          <ul className="divide-y-2 divide-black border-2 border-black">
            {hooks.map((h) => (
              <li
                key={h.id}
                className="px-3 py-2 flex items-start justify-between gap-3"
                data-testid={`webhook-row-${h.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-sm truncate">
                    <span className="text-[#FF2A2A]">{h.id.slice(0, 8)}</span> · {h.url}
                  </p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {h.events.map((ev) => (
                      <span key={ev} className="chip text-[10px]">{ev}</span>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => removeHook(h.id)}
                  className="btn-flat-outline shrink-0"
                  data-testid={`webhooks-delete-${h.id}`}
                  aria-label={`Delete webhook ${h.id}`}
                >
                  <Trash weight="bold" size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* SECTION C — Fire test */}
      <section className="hairline-2 mb-6 p-4" data-testid="webhooks-fire-section">
        <h2 className="font-display font-black text-2xl tracking-tight mb-3 flex items-center gap-2">
          <Lightning weight="fill" size={20} /> FIRE TEST EVENT
        </h2>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="label-mono block mb-1">TARGET WEBHOOK</label>
            <select
              data-testid="fire-webhook-select"
              className="w-full px-3 py-2 border-2 border-black font-mono text-sm bg-white"
              value={testHookId}
              onChange={(e) => setTestHookId(e.target.value)}
            >
              <option value="">— pick a webhook —</option>
              {hooks.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.id.slice(0, 8)} · {h.url}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-mono block mb-1">EVENT</label>
            <select
              data-testid="fire-event-select"
              className="w-full px-3 py-2 border-2 border-black font-mono text-sm bg-white"
              value={testEvent}
              onChange={(e) => { setTestEvent(e.target.value); setSourceEventId(""); }}
            >
              {catalogue.map((e) => (
                <option key={e.event} value={e.event}>{e.event}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex gap-2 mb-3" data-testid="fire-mode-toggle">
          <button
            onClick={() => setTestMode("synthetic")}
            className={testMode === "synthetic" ? "btn-flat" : "btn-flat-outline"}
            data-testid="fire-mode-synthetic"
          >
            SYNTHETIC
          </button>
          <button
            onClick={() => setTestMode("real")}
            className={testMode === "real" ? "btn-flat" : "btn-flat-outline"}
            data-testid="fire-mode-real"
          >
            REAL · SNAPSHOT REPLAY
          </button>
        </div>

        {testMode === "synthetic" && (
          <div className="mb-3 p-3 border-2 border-dashed border-black" data-testid="fire-synthetic-options">
            <p className="font-mono text-xs text-neutral-700 mb-2">
              <strong>Synthetic</strong> — fakes the resource payload, delivers ONLY to the
              selected webhook. By default does NOT touch the audit log.
            </p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="fire-persist-toggle"
                checked={persist}
                onChange={(e) => setPersist(e.target.checked)}
                className="w-4 h-4 border-2 border-black"
              />
              <span className="label-mono">PERSIST TO AUDIT LOG</span>
              <span className="font-mono text-[11px] text-neutral-500">
                — explicit opt-in to write into application_events with sandbox provenance
              </span>
            </label>
          </div>
        )}

        {testMode === "real" && (
          <div className="mb-3 p-3 border-2 border-dashed border-black" data-testid="fire-real-options">
            <p className="font-mono text-xs text-neutral-700 mb-2">
              <strong>Snapshot Replay</strong> — re-sends a recorded envelope from{" "}
              <code>application_events</code> verbatim (no re-derive, no re-persist, no
              session/photo mutation).
            </p>
            <label className="label-mono block mb-1">SOURCE EVENT (audit log)</label>
            <select
              data-testid="fire-source-select"
              className="w-full px-3 py-2 border-2 border-black font-mono text-sm bg-white"
              value={sourceEventId}
              onChange={(e) => setSourceEventId(e.target.value)}
            >
              <option value="">— latest matching '{testEvent}' (auto) —</option>
              {sources.map((s) => (
                <option key={s.event_id} value={s.event_id}>
                  {fmtFullIso(new Date(s.ts * 1000).toISOString())} · {s.event_id.slice(0, 8)} ·{" "}
                  {(s.resource?.filename || s.resource?.name || s.resource?.id || "").toString().slice(0, 40)}
                </option>
              ))}
            </select>
            <p className="font-mono text-[10px] text-neutral-500 mt-1">
              sandbox-originated events are excluded from this list to prevent QA amplification.
              {sources.length === 0 && " · no source events available yet — fire a synthetic with PERSIST or wait for organic traffic"}
            </p>
          </div>
        )}

        <button
          onClick={fireTest}
          disabled={firing}
          className="btn-flat"
          data-testid="fire-test-btn"
        >
          <Lightning weight="fill" size={16} />
          {firing ? "FIRING…" : testMode === "real" ? "Replay" : "Fire Test"}
        </button>
      </section>

      {/* SECTION D — Recent deliveries */}
      <section className="hairline-2 p-4" data-testid="webhooks-deliveries-section">
        <h2 className="font-display font-black text-2xl tracking-tight mb-3 flex items-center gap-2">
          <CheckCircle weight="bold" size={20} /> RECENT DELIVERIES ({deliveries.length})
        </h2>
        {deliveries.length === 0 ? (
          <p className="font-mono text-sm text-neutral-500">no deliveries yet</p>
        ) : (
          <ul className="divide-y-2 divide-black border-2 border-black">
            {deliveries.map((d) => {
              const meta = STATUS_CHIP[d.status] || { cls: "chip", label: (d.status || "?").toUpperCase() };
              const canReplay = REPLAYABLE.has(d.status);
              const sandbox = d.sandbox || null;
              return (
                <li
                  key={d.id}
                  className="px-3 py-2 flex items-start gap-3 flex-wrap"
                  data-testid={`delivery-row-${d.id}`}
                >
                  <span className={meta.cls} data-testid={`delivery-status-${d.id}`}>
                    {d.status === "dead" && <Skull weight="fill" size={12} className="inline mr-1" />}
                    {d.status === "failed" && <WarningOctagon weight="fill" size={12} className="inline mr-1" />}
                    {meta.label}
                  </span>
                  {sandbox && (
                    <span
                      className="chip chip-blue text-[10px]"
                      data-testid={`delivery-sandbox-${d.id}`}
                      title={`mode=${sandbox.mode} source=${sandbox.source}${sandbox.replayed_from_event_id ? " from=" + sandbox.replayed_from_event_id.slice(0,8) : ""}${sandbox.persisted_to_audit ? " · persisted" : ""}`}
                    >
                      SANDBOX · {(sandbox.source || sandbox.mode || "").toUpperCase()}
                    </span>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-xs">
                      <span className="text-[#FF2A2A]">{d.id.slice(0, 8)}</span> · {d.event}
                      <span className="opacity-60"> · attempts {d.attempts || 0}</span>
                      {d.last_status != null && (
                        <span className="opacity-60"> · http {d.last_status}</span>
                      )}
                    </p>
                    <p className="font-mono text-[11px] opacity-70 truncate">→ {d.url}</p>
                    <p
                      className="font-mono text-[10px] opacity-60"
                      data-testid={`delivery-trace-${d.id}`}
                    >
                      EMIT {fmtIso(d.emitted_at || d.created_at)}
                      {d.leased_at && ` → LEASE ${fmtIso(d.leased_at)}`}
                      {d.attempted_at && ` → ATTEMPT ${fmtIso(d.attempted_at)}`}
                      {d.responded_at && ` → RESP ${fmtIso(d.responded_at)}`}
                      {d.next_attempt_at && ` · NEXT ${fmtFullIso(d.next_attempt_at)}`}
                      {d.replayed_at && ` · REPLAYED ${fmtIso(d.replayed_at)}`}
                    </p>
                    {sandbox?.replayed_from_event_id && (
                      <p className="font-mono text-[10px] text-[#0024D6]">
                        ↳ replayed from event{" "}
                        <span className="font-bold">{sandbox.replayed_from_event_id.slice(0, 8)}</span>
                      </p>
                    )}
                    {d.last_error && (
                      <p className="font-mono text-[10px] text-[#FF2A2A] truncate">err: {d.last_error}</p>
                    )}
                  </div>
                  {canReplay && (
                    <button
                      onClick={() => replay(d.id)}
                      className="btn-flat-outline shrink-0"
                      data-testid={`delivery-replay-${d.id}`}
                      title="Replay this delivery"
                    >
                      <ArrowUUpLeft weight="bold" size={14} />
                      Replay
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <p className="text-xs text-neutral-500 mt-4 font-mono">
        sandbox is a CLIENT of the event system, never a writer of production truth.
        real-mode replay re-sends the recorded envelope verbatim — no DB re-derive,
        no audit re-write. synthetic mode requires explicit <code>persist=true</code>{" "}
        to touch the audit log.
      </p>
    </div>
  );
}
